import axios from 'axios';
import { useFormik } from 'formik';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import * as yup from 'yup';

import { Button, RichText } from 'components/UI';
import api from 'data/api.json';
import contact from 'data/contact.json';
import analytics from 'data/analytics.json';
import pricing from 'data/pricing.json';
import { MediaQueries } from 'environment';
import { useMediaQuery } from 'hooks';

import {
	BaitInput,
	Container,
	Disclaimer,
	DropdownInput,
	Error,
	InputWrapper,
	Link,
	Row,
	TextInput,
	Title
} from './ContactForm.style';

const validationSchema = yup.object().shape({
	name: yup.string().required(contact.form.name.required),
	email: yup.string().email(contact.form.email.invalid).required(contact.form.email.required),
	phone: yup.string().required(contact.form.phone.required),
	plan: yup.string(),
	message: yup.string().required(contact.form.message.required)
});

interface Props {
	title?: string;
	plan?: string;
}

export function ContactForm({ title, plan }: Props) {
	const isPhone = useMediaQuery(MediaQueries.phone);
	const isTablet = useMediaQuery(MediaQueries.tablet);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);

	const {
		errors,
		handleBlur,
		handleChange,
		handleSubmit,
		isValid,
		setFieldValue,
		touched,
		values
	} = useFormik({
		initialValues: {
			name: '',
			phone: '',
			email: '',
			plan: `${plan ?? 'Starter'} plan`,
			message: '',
			bait: ''
		},
		validationSchema,
		onSubmit: async values => {
			if (!values.bait) {
				const { bait: _, plan, name, phone, email, message } = values;
				const formData = new FormData();
				formData.append('source', `${api.contactSource}`);
				formData.append('name', name);
				formData.append('email', email);
				formData.append('phone', phone);
				formData.append('message', plan.concat(` - ${message}`));
				formData.append('key', api.contactKey);

				try {
					setError(false);
					setLoading(true);

					const response = await axios.post(api.contact, formData, {
						headers: {
							'Content-Type': 'application/x-www-form-urlencoded'
						}
					});

					if (response.status === 201 || response.status === 200) {
						navigate('/thanks/');
					} else {
						setError(true);
					}
				} catch (err) {
					console.log(err);
					setError(true);
				} finally {
					setLoading(false);
				}
			}
		}
	});

	return (
		<Container>
			{title && <Title>{title}</Title>}
			<TextInput
				error={touched.name ? errors.name : undefined}
				label={contact.form.name.label}
				name="name"
				placeholder={contact.form.name.placeholder}
				required
				type="text"
				value={values.name}
				onBlur={handleBlur}
				onChange={handleChange}
			/>
			<Row>
				<InputWrapper>
					<TextInput
						error={touched.email ? errors.email : undefined}
						label={contact.form.email.label}
						name="email"
						placeholder={contact.form.email.placeholder}
						required
						type="email"
						value={values.email}
						onBlur={handleBlur}
						onChange={handleChange}
					/>
				</InputWrapper>
				<InputWrapper>
					<TextInput
						error={touched.phone ? errors.phone : undefined}
						label={contact.form.phone.label}
						name="phone"
						placeholder={contact.form.phone.placeholder}
						required
						type="phone"
						value={values.phone}
						onBlur={handleBlur}
						onChange={handleChange}
					/>
				</InputWrapper>
			</Row>
			{plan && (
				<DropdownInput selected={values.plan} label={contact.form.plan.label}>
					<>
						{pricing.plans.map(plan => (
							<DropdownInput.Item
								key={`${plan.type}-plan-item`}
								onClick={() => setFieldValue('plan', `${plan.type} plan`)}
							>
								{plan.type} plan
							</DropdownInput.Item>
						))}
					</>
					<DropdownInput.Item onClick={() => setFieldValue('plan', 'Custom plan')}>
						Custom plan
					</DropdownInput.Item>
				</DropdownInput>
			)}
			<TextInput
				error={touched.message ? errors.message : undefined}
				label={contact.form.message.label}
				multiline
				name="message"
				placeholder={contact.form.message.placeholder}
				required
				type="text"
				value={values.message}
				onBlur={handleBlur}
				onChange={handleChange}
			/>
			<BaitInput
				name="bait"
				required
				tabIndex={-1}
				type="hidden"
				value={values.bait}
				onBlur={handleBlur}
				onChange={handleChange}
			/>
			<Disclaimer>
				{contact.form.disclaimer}&nbsp;
				<RichText>
					<Link to="/terms/">{contact.form.terms}</Link>
				</RichText>
				&nbsp;and&nbsp;
				<RichText>
					<Link to="/privacy/">{contact.form.privacyPolicy}</Link>
				</RichText>
			</Disclaimer>
			<Button
				name={analytics.contactSend}
				disabled={!isValid}
				loading={loading}
				type="submit"
				width={isTablet ? 30 : !isPhone ? 28 : '100%'}
				onClick={handleSubmit}
			>
				{contact.form.button}
			</Button>
			{error && <Error>Sorry, something went wrong, please contact us by email.</Error>}
		</Container>
	);
}
